/**
 * Constants Dashboard Frontend
 * Phase 3.2 - Centralisation complète
 */

// Constantes de localisation
export const LOCALES = {
  FR_FR: 'fr-FR',
  EN_US: 'en-US'
};

// Textes d'interface
export const UI_TEXTS = {
  NIGHTS: 'nuit(s)',
  ALL: 'Toutes',
  PENDING: 'En attente',
  CONFIRMED: 'Confirmées', 
  ACTIVE: 'Actives',
  COMPLETED: 'Terminées',
  CANCELLED: 'Annulées',
  RENTER: 'Locataire',
  NO_TITLE: 'Propriété sans titre',
  NO_ADDRESS: 'Adresse non disponible',
  NO_RENTER: 'N/A'
};

// Types de dashboard
export const DASHBOARD_TYPES = {
  OWNER: 'owner',
  RENTER: 'renter'
};

// Widgets disponibles
export const WIDGET_TYPES = {
  OVERVIEW: 'overview',
  RESERVATIONS: 'reservations',
  REVENUE: 'revenue',
  AVAILABILITY: 'availability',
  LISTINGS: 'listings',
  CALENDAR: 'calendar',
  STATS: 'stats',
  QUICK_ACTIONS: 'quick_actions',
  MY_RESERVATIONS: 'my_reservations',
  FAVORITES: 'favorites',
  SEARCH: 'search',
  RECOMMENDATIONS: 'recommendations'
};

// Périodes de statistiques
export const STATS_PERIODS = {
  TODAY: 'today',
  WEEK: 'week',
  MONTH: 'month',
  QUARTER: 'quarter',
  YEAR: 'year',
  CUSTOM: 'custom'
};

// Labels des périodes
export const PERIOD_LABELS = {
  [STATS_PERIODS.TODAY]: "Aujourd'hui",
  [STATS_PERIODS.WEEK]: 'Cette semaine',
  [STATS_PERIODS.MONTH]: 'Ce mois',
  [STATS_PERIODS.QUARTER]: 'Ce trimestre',
  [STATS_PERIODS.YEAR]: 'Cette année',
  [STATS_PERIODS.CUSTOM]: 'Personnalisé'
};

// Métriques owner
export const OWNER_METRICS = {
  TOTAL_REVENUE: 'total_revenue',
  PENDING_REVENUE: 'pending_revenue',
  ACTIVE_RESERVATIONS: 'active_reservations',
  TOTAL_LISTINGS: 'total_listings',
  AVAILABLE_DAYS: 'available_days',
  BOOKED_DAYS: 'booked_days',
  OCCUPANCY_RATE: 'occupancy_rate',
  AVERAGE_RATING: 'average_rating'
};

// Métriques renter
export const RENTER_METRICS = {
  ACTIVE_RESERVATIONS: 'active_reservations',
  COMPLETED_RESERVATIONS: 'completed_reservations',
  TOTAL_SPENT: 'total_spent',
  FAVORITE_LISTINGS: 'favorite_listings',
  UPCOMING_TRIPS: 'upcoming_trips'
};

// Labels des métriques owner
export const OWNER_METRIC_LABELS = {
  [OWNER_METRICS.TOTAL_REVENUE]: 'Revenus totaux',
  [OWNER_METRICS.PENDING_REVENUE]: 'Revenus en attente',
  [OWNER_METRICS.ACTIVE_RESERVATIONS]: 'Réservations actives',
  [OWNER_METRICS.TOTAL_LISTINGS]: 'Annonces totales',
  [OWNER_METRICS.AVAILABLE_DAYS]: 'Jours disponibles',
  [OWNER_METRICS.BOOKED_DAYS]: 'Jours réservés',
  [OWNER_METRICS.OCCUPANCY_RATE]: 'Taux d\'occupation',
  [OWNER_METRICS.AVERAGE_RATING]: 'Note moyenne'
};

// Labels des métriques renter
export const RENTER_METRIC_LABELS = {
  [RENTER_METRICS.ACTIVE_RESERVATIONS]: 'Réservations actives',
  [RENTER_METRICS.COMPLETED_RESERVATIONS]: 'Réservations terminées',
  [RENTER_METRICS.TOTAL_SPENT]: 'Total dépensé',
  [RENTER_METRICS.FAVORITE_LISTINGS]: 'Annonces favorites',
  [RENTER_METRICS.UPCOMING_TRIPS]: 'Voyages à venir'
};

// Couleurs par statut (synchronized with backend)
export const STATUS_COLORS = {
  // Réservations
  pending: '#ff9800',
  confirmed: '#2196f3',
  active: '#4caf50',
  completed: '#9c27b0',
  cancelled: '#f44336',
  expired: '#757575',
  
  // Paiements
  payment_pending: '#ff9800',
  payment_completed: '#4caf50',
  payment_failed: '#f44336',
  payment_partial: '#ff5722',
  payment_refunded: '#9c27b0',
  
  // Disponibilités
  disponible: '#4caf50',
  bloquee: '#f44336',
  
  // Success/Warning/Error generics
  success: '#4caf50',
  warning: '#ff9800',
  error: '#f44336',
  info: '#2196f3'
};

// Icônes par métrique
export const METRIC_ICONS = {
  [OWNER_METRICS.TOTAL_REVENUE]: 'AttachMoney',
  [OWNER_METRICS.PENDING_REVENUE]: 'Schedule',
  [OWNER_METRICS.ACTIVE_RESERVATIONS]: 'Event',
  [OWNER_METRICS.TOTAL_LISTINGS]: 'Home',
  [OWNER_METRICS.AVAILABLE_DAYS]: 'CalendarMonth',
  [OWNER_METRICS.BOOKED_DAYS]: 'EventBusy',
  [OWNER_METRICS.OCCUPANCY_RATE]: 'TrendingUp',
  [OWNER_METRICS.AVERAGE_RATING]: 'Star',
  
  [RENTER_METRICS.ACTIVE_RESERVATIONS]: 'Event',
  [RENTER_METRICS.COMPLETED_RESERVATIONS]: 'CheckCircle',
  [RENTER_METRICS.TOTAL_SPENT]: 'AttachMoney',
  [RENTER_METRICS.FAVORITE_LISTINGS]: 'Favorite',
  [RENTER_METRICS.UPCOMING_TRIPS]: 'FlightTakeoff'
};

// Configuration des widgets par défaut
export const DEFAULT_WIDGET_LAYOUTS = {
  [DASHBOARD_TYPES.OWNER]: [
    { i: 'overview', x: 0, y: 0, w: 12, h: 4 },
    { i: 'revenue', x: 0, y: 4, w: 6, h: 6 },
    { i: 'reservations', x: 6, y: 4, w: 6, h: 6 },
    { i: 'availability', x: 0, y: 10, w: 8, h: 8 },
    { i: 'quick_actions', x: 8, y: 10, w: 4, h: 8 }
  ],
  [DASHBOARD_TYPES.RENTER]: [
    { i: 'overview', x: 0, y: 0, w: 12, h: 4 },
    { i: 'my_reservations', x: 0, y: 4, w: 8, h: 6 },
    { i: 'quick_actions', x: 8, y: 4, w: 4, h: 6 },
    { i: 'search', x: 0, y: 10, w: 6, h: 6 },
    { i: 'favorites', x: 6, y: 10, w: 6, h: 6 }
  ]
};

// Actions rapides
export const QUICK_ACTIONS = {
  [DASHBOARD_TYPES.OWNER]: [
    { 
      action: 'add_listing', 
      label: 'Ajouter une annonce', 
      icon: 'AddBusiness',
      color: 'primary',
      route: '/owner/listings/new'
    },
    { 
      action: 'block_dates', 
      label: 'Bloquer des dates', 
      icon: 'Block',
      color: 'warning',
      route: '/owner/calendar'
    },
    { 
      action: 'view_calendar', 
      label: 'Calendrier unifié', 
      icon: 'CalendarMonth',
      color: 'info',
      route: '/owner/calendar'
    },
    { 
      action: 'export_data', 
      label: 'Exporter les données', 
      icon: 'Download',
      color: 'secondary'
    }
  ],
  [DASHBOARD_TYPES.RENTER]: [
    { 
      action: 'search_listings', 
      label: 'Rechercher', 
      icon: 'Search',
      color: 'primary',
      route: '/search'
    },
    { 
      action: 'view_reservations', 
      label: 'Mes réservations', 
      icon: 'Event',
      color: 'info',
      route: '/renter/reservations'
    },
    { 
      action: 'view_favorites', 
      label: 'Mes favoris', 
      icon: 'Favorite',
      color: 'error',
      route: '/renter/favorites'
    },
    { 
      action: 'manage_profile', 
      label: 'Mon profil', 
      icon: 'Person',
      color: 'secondary',
      route: '/profile'
    }
  ]
};

// Formatters
export const formatCurrency = (amount, currency = 'EUR') => {
  return new Intl.NumberFormat('fr-FR', { 
    style: 'currency', 
    currency: currency || 'EUR'
  }).format(amount || 0);
};

export const formatPercentage = (value) => {
  return `${Math.round(value || 0)}%`;
};

export const formatNumber = (value) => {
  return new Intl.NumberFormat('fr-FR').format(value || 0);
};

export const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};
